<template>
  <sp-card :variant="cardVariant" class="entry-card" :class="modifierClasses">
    <sp-card-content>
      <sp-row align-content="space-between" height="100%">
        <sp-container>
          <sp-text :type="cardTitleTypeScale">{{ cardTitle }}</sp-text>
          <sp-footnote>{{ cardSubtitle }}</sp-footnote>
        </sp-container>

        <template v-if="isMobile">
          <sp-button class="show-more" variant="outlined" size="x-small" no-wrap block @click="showMoreSlots">
            {{ slotsCount }} appts
          </sp-button>
        </template>

        <sp-container v-else flex direction="column" :row-gap="toCssSpacingProperty(4)">
          <sp-row v-for="(slot, index) in visibleSlots" :key="index">
            <sp-col>
              <sp-button
                tag="a"
                variant="outlined"
                size="x-small"
                no-wrap
                block
                :title="slot.visit_type_categories_description"
                :href="slot.booking_url"
              >
                {{ formatStartTime(slot.start_time) }}
              </sp-button>
            </sp-col>
          </sp-row>
          <sp-row v-if="hasMoreSlots">
            <sp-col>
              <sp-button size="x-small" variant="outlined" no-wrap block @click="showMoreSlots">
                {{ moreSlotsCount }} more
              </sp-button>
            </sp-col>
          </sp-row>
        </sp-container>
      </sp-row>
    </sp-card-content>
  </sp-card>
</template>

<script setup>
import moment from "moment";
import { computed } from "vue";
import { toCssSpacingProperty } from "../../../utils/props";

const emit = defineEmits(["select-slot", "show-more-slots"]);

const props = defineProps({
  /**
   * The entry object to display.
   *
   * @type {Object}
   * @property {String} date The date of the entry.
   * @property {Array} slots The slots for the entry.
   * @property {String} slots.start_time The start time of the slot.
   * @property {String} slots.booking_url The URL to book the slot.
   * @property {String} slots.visit_type_categories_description The description of the visit type.
   *
   * @example
   * {
   *  date: "2021-09-01",
   *  slots: [{
   *   start_time: "2021-09-01T09:00:00Z",
   *   booking_url: "https://example.com",
   *   visit_type_categories_description: "New Patient"
   *  }],
   * }
   *
   * @required true
   */
  entry: {
    type: Object,
    required: true,
  },
  /**
   * Whether the card is being displayed on a mobile device.
   * We use the parent object to determine this in order to avoid having too many breakpoint listeners.
   *
   * @type {Boolean}
   * @default false
   */
  isMobile: {
    type: Boolean,
    default: false,
  },
  /**
   * The maximum number of slots to display.
   *
   * This is only used when the card is being displayed on a non-mobile device.
   * Mobile devices will always display a summary.
   * The rest of the slots will be hidden and a "Show more" button will be displayed.
   *
   * @type {Number}
   * @default 2
   */
  maxSlots: {
    type: Number,
    default: 2,
  },
});

const date = computed(() => moment(props.entry.date));

const cardVariant = computed(() => (props.isMobile ? "outlined" : "elevated"));
const cardTitle = computed(() => date.value.format("MMM D"));
const cardTitleTypeScale = computed(() => (props.isMobile ? "body-bold" : "h4"));
const cardSubtitle = computed(() => date.value.format(props.isMobile ? "ddd" : "dddd"));
const modifierClasses = computed(() => ({ "--is-mobile": props.isMobile }));

const slots = computed(() => props.entry.slots);
const slotsCount = computed(() => slots.value.length);
const visibleSlotsCount = computed(() => (props.isMobile ? 0 : props.maxSlots));
const visibleSlots = computed(() => slots.value.slice(0, visibleSlotsCount.value));
const moreSlotsCount = computed(() => slotsCount.value - visibleSlots.value.length);
const hasMoreSlots = computed(() => moreSlotsCount.value > 0);

function formatStartTime(startTime) {
  return moment(startTime).format("h:mm A");
}

function showMoreSlots() {
  emit("show-more-slots", { date: props.entry.date });
}
</script>

<style lang="scss" scoped>
.entry-card {
  --padding-block: var(--sp-ref-spacing-12);
  --padding-inline: var(--padding-block);
  --sp-comp-card-padding-block: var(--padding-block);
  --sp-comp-card-padding-inline: var(--padding-inline);
  --sp-comp-card-border: var(--sp-comp-slot-cards-border, 1px solid var(--sp-sys-color-outline-variant));
  --sp-comp-card-content-height: calc(var(--sp-comp-slot-cards-height, 16rem) - var(--padding-block) * 2);
  --sp-comp-card-border-radius: var(--sp-comp-slot-cards-border-radius, var(--sp-ref-border-radius-xs));

  &[variant="outlined"] {
    --border-color: var(--sp-comp-slot-cards-outlined-border-color, var(--sp-sys-color-primary));
    --border-width: var(--sp-comp-slot-cards-outlined-border-width, 2px);
    --sp-comp-card-outlined-border: var(--border-width) solid var(--border-color);
    --sp-comp-card-content-height: calc(
      var(--sp-comp-slot-cards-mobile-height, 10rem) - var(--sp-comp-card-padding-block) * 2
    );
  }

  &.--is-mobile {
    --padding-block: var(--sp-ref-spacing-8);
    --padding-inline: var(--padding-block);
    --sp-comp-card-content-height: calc(
      var(--sp-comp-slot-cards-mobile-height, 8.5rem) - var(--sp-comp-card-padding-block) * 2
    );
    .show-more {
      --sp-comp-button-font-short: var(--sp-sys-typescale-body-semi-bold-short);
    }
  }
}

sp-card-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
</style>
